import { CircularProgress, Box } from '@mui/material';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from './useAuth';
import type { Role } from './roles';

export interface ProtectedRouteProps {
  readonly requiresRole?: Role;
}

/**
 * Route guard for authenticated areas. Redirects to `/login` when the
 * user has no session, and renders a `Forbidden` placeholder when they
 * lack the required role.
 */
export const ProtectedRoute = ({ requiresRole }: ProtectedRouteProps): JSX.Element => {
  const { status, hasRole } = useAuth();
  const location = useLocation();

  if (status === 'loading') {
    return (
      <Box className="flex h-full w-full items-center justify-center p-12">
        <CircularProgress aria-label="Loading session" />
      </Box>
    );
  }

  if (status === 'unauthenticated') {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (requiresRole && !hasRole(requiresRole)) {
    return (
      <Box className="p-8">
        <h1 className="text-xl font-semibold">Forbidden</h1>
        <p className="text-qflyInk-700">
          You do not have permission to view this page. Contact your administrator if you believe
          this is an error.
        </p>
      </Box>
    );
  }

  return <Outlet />;
};
